import * as React from 'react'
import styled from 'styled-components'
import { config } from 'react-awesome-styled-grid'
import { Navigation, Pagination, A11y, EffectFade } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { H5, P, colors } from './shared'
import { LeftSliderArrow } from './shared/icons/LeftSliderArrow'
import { RightSliderArrow } from './shared/icons/RightSliderArrow'
import { Stars } from './UserQuotesSlider'
import { OutlineOnFocusVisible } from './shared/buttons'
import { isChromeForIOs145 } from '@helpers/utils'

export type ReviewType = {
  quote: string,
  subText?: string,
  audioUrl: string,
  name: string,
  stars?: number,
  // set by models/ThematicLandingPage.getUserQuotes()
  key?: string
}

type PropsType = {
  slides: ReviewType[]
}

function customPagination (props: { className: string, index: number }): string {
  return `<span class="swiper-pagination-border ${props.className}" style="${
    props.className.includes('active') ? 'background-color: red;' : ''
  }"></span>`
}

export default function ReviewSlider (props: PropsType): React.ReactNode {
  return (
    <Container>
      <MobileSlider>
        <Swiper
          modules={[Navigation, Pagination, A11y, EffectFade]}
          spaceBetween={0}
          slidesPerView={1}
          draggable={true}
          pagination={{
            clickable: true,
            type: 'bullets',
            renderBullet: (index: number, className: string): string =>
              customPagination({ index, className })
          }}
          cssMode={isChromeForIOs145()}
        >
          {props.slides &&
            props.slides.map((slide: ReviewType, index: number): React.ReactNode => {
              return (
                <SwiperSlide key={`review-slider-${index}`}>
                  <QuoteContainer>
                    <svg
                      width="24"
                      height="22"
                      viewBox="0 0 24 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.54453 0.499995L9.85758 0.34347L9.71068 0.0496659L9.40816 0.177656L9.54453 0.499995ZM9.94453 1.3L10.1357 1.59316L10.3972 1.42265L10.2576 1.14347L9.94453 1.3ZM6.24453 5L6.53581 5.19419L6.53981 5.1879L6.24453 5ZM5.24453 10.4L4.99704 10.6475L5.01463 10.6651L5.03453 10.68L5.24453 10.4ZM6.64453 11L6.54396 11.3352L6.55175 11.3376L6.55964 11.3395L6.64453 11ZM9.34453 18.7L9.58212 18.9574L9.59202 18.9475L9.34453 18.7ZM2.04453 18.2L1.78195 18.4317L1.78966 18.4399L2.04453 18.2ZM1.64453 8.39999L1.32669 8.2533L1.32417 8.25904L1.64453 8.39999ZM4.84453 3.7L4.59697 3.45243L4.59108 3.45862L4.84453 3.7ZM22.3445 0.499995L22.6576 0.34347L22.5107 0.0496659L22.2082 0.177656L22.3445 0.499995ZM22.7445 1.3L22.9357 1.59316L23.1972 1.42265L23.0576 1.14347L22.7445 1.3ZM19.0445 5L19.3358 5.19419L19.3398 5.1879L19.0445 5ZM18.0445 10.4L17.797 10.6475L17.8146 10.6651L17.8345 10.68L18.0445 10.4ZM19.4445 11L19.344 11.3352L19.3518 11.3376L19.3596 11.3395L19.4445 11ZM22.1445 18.7L22.3821 18.9574L22.392 18.9475L22.1445 18.7ZM14.8445 18.2L14.582 18.4317L14.5897 18.4399L14.8445 18.2ZM14.4445 8.39999L14.1267 8.2533L14.1242 8.25904L14.4445 8.39999ZM17.6445 3.7L17.397 3.45243L17.3911 3.45862L17.6445 3.7ZM9.23148 0.656519L9.63148 1.45652L10.2576 1.14347L9.85758 0.34347L9.23148 0.656519ZM9.75334 1.00683C8.18046 2.03262 6.91079 3.3011 5.94925 4.81209L6.53981 5.1879C7.44494 3.76556 8.64193 2.56737 10.1357 1.59316L9.75334 1.00683ZM5.95331 4.80585C4.9895 6.25157 4.49453 7.71799 4.49453 9.2H5.19453C5.19453 7.88201 5.6329 6.54842 6.53575 5.19414L5.95331 4.80585ZM4.49453 9.2C4.49453 9.78728 4.64159 10.292 4.99704 10.6475L5.49202 10.1525C5.31414 9.97463 5.19453 9.67938 5.19453 9.2H4.49453ZM5.03453 10.68C5.35368 10.9194 5.87125 11.1334 6.54396 11.3352L6.7451 10.6648C6.08448 10.4666 5.66871 10.2806 5.45453 10.12L5.03453 10.68ZM6.55964 11.3395C7.851 11.6624 8.78014 12.1695 9.38555 12.8354C9.98523 13.4951 10.2945 14.3404 10.2945 15.4H10.9945C10.9945 14.193 10.6372 13.1716 9.90351 12.3646C9.17559 11.5639 8.10473 11.0043 6.72942 10.6604L6.55964 11.3395ZM10.2945 15.4C10.2945 16.6495 9.89198 17.6576 9.09704 18.4525L9.59202 18.9475C10.5304 18.0091 10.9945 16.8172 10.9945 15.4H10.2945ZM9.10713 18.4428C8.31366 19.1752 7.30224 19.55 6.04453 19.55V20.25C7.45349 20.25 8.64207 19.8247 9.58193 18.9572L9.10713 18.4428ZM6.04453 19.55C4.53798 19.55 3.29786 19.021 2.2994 17.9601L1.78966 18.4399C2.92454 19.6457 4.35108 20.25 6.04453 20.25V19.55ZM2.30698 17.9684C1.37773 16.9153 0.894531 15.4725 0.894531 13.6H0.194531C0.194531 15.5941 0.711331 17.218 1.78209 18.4316L2.30698 17.9684ZM0.894531 13.6C0.894531 11.8491 1.25087 10.1637 1.96489 8.54095L1.32417 8.25904C0.571526 9.9696 0.194531 11.7509 0.194531 13.6H0.894531ZM1.96232 8.54666C2.74601 6.84867 3.7908 5.31391 5.09798 3.94137L4.59108 3.45862C3.23159 4.88608 2.14305 6.48466 1.32675 8.25332L1.96232 8.54666ZM5.09202 3.94748C6.46183 2.57768 7.99122 1.5372 9.68091 0.822333L9.40816 0.177656C7.63118 0.929458 6.02724 2.02232 4.59704 3.45251L5.09202 3.94748ZM22.0315 0.656519L22.4315 1.45652L23.0576 1.14347L22.6576 0.34347L22.0315 0.656519ZM22.5533 1.00683C20.9805 2.03262 19.7108 3.3011 18.7493 4.81209L19.3398 5.1879C20.2449 3.76556 21.4419 2.56737 22.9357 1.59316L22.5533 1.00683ZM18.7533 4.80585C17.7895 6.25157 17.2945 7.71799 17.2945 9.2H17.9945C17.9945 7.88201 18.4329 6.54842 19.3358 5.19414L18.7533 4.80585ZM17.2945 9.2C17.2945 9.78728 17.4416 10.292 17.797 10.6475L18.292 10.1525C18.1141 9.97463 17.9945 9.67938 17.9945 9.2H17.2945ZM17.8345 10.68C18.1537 10.9194 18.6712 11.1334 19.344 11.3352L19.5451 10.6648C18.8845 10.4666 18.4687 10.2806 18.2545 10.12L17.8345 10.68ZM19.3596 11.3395C20.651 11.6624 21.5801 12.1695 22.1856 12.8354C22.7852 13.4951 23.0945 14.3404 23.0945 15.4H23.7945C23.7945 14.193 23.4372 13.1716 22.7035 12.3646C21.9756 11.5639 20.9047 11.0043 19.5294 10.6604L19.3596 11.3395ZM23.0945 15.4C23.0945 16.6495 22.692 17.6576 21.897 18.4525L22.392 18.9475C23.3304 18.0091 23.7945 16.8172 23.7945 15.4H23.0945ZM21.9071 18.4428C21.1137 19.1752 20.1022 19.55 18.8445 19.55V20.25C20.2535 20.25 21.4421 19.8247 22.3819 18.9572L21.9071 18.4428ZM18.8445 19.55C17.338 19.55 16.0979 19.021 15.0994 17.9601L14.5897 18.4399C15.7245 19.6457 17.1511 20.25 18.8445 20.25V19.55ZM15.107 17.9684C14.1777 16.9153 13.6945 15.4725 13.6945 13.6H12.9945C12.9945 15.5941 13.5113 17.218 14.5821 18.4316L15.107 17.9684ZM13.6945 13.6C13.6945 11.8491 14.0509 10.1637 14.7649 8.54095L14.1242 8.25904C13.3715 9.9696 12.9945 11.7509 12.9945 13.6H13.6945ZM14.7623 8.54666C15.546 6.84867 16.5908 5.31391 17.898 3.94137L17.3911 3.45862C16.0316 4.88608 14.9431 6.48466 14.1267 8.25332L14.7623 8.54666ZM17.892 3.94748C19.2618 2.57768 20.7912 1.5372 22.4809 0.822333L22.2082 0.177656C20.4312 0.929458 18.8272 2.02232 17.397 3.45251L17.892 3.94748Z"
                        fill={colors.sand}
                      />
                    </svg>
                  </QuoteContainer>
                  <QuoteText>“{slide.quote}”</QuoteText>
                  <Stars />
                  <CustomerInfo>
                    {slide.name} {!!slide.subText && `- ${slide.subText}`}
                  </CustomerInfo>
                </SwiperSlide>
              )
            })}
        </Swiper>
      </MobileSlider>
      <DesktopSlider>
        <ArrowWrapper className="slidePrev-btn-review-slider">
          <LeftSliderArrow color={colors.sand} size={57} />
        </ArrowWrapper>
        <Swiper
          modules={[Navigation, Pagination, A11y, EffectFade]}
          spaceBetween={0}
          slidesPerView={1}
          draggable={true}
          effect="fade"
          fadeEffect={{
            crossFade: true
          }}
          navigation={{
            prevEl: '.slidePrev-btn-review-slider',
            nextEl: '.slideNext-btn-review-slider'
          }}
          pagination={{
            clickable: true,
            type: 'bullets',
            renderBullet: (index: number, className: string): string =>
              customPagination({ index, className })
          }}
        >
          {props.slides &&
            props.slides.map((slide: ReviewType, index: number): React.ReactNode => {
              return (
                <SwiperSlide key={`review-slider-${index}`}>
                  <QuoteContainer>
                    <svg
                      width="42.22"
                      height="36.06"
                      viewBox="0 0 24 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.54453 0.499995L9.85758 0.34347L9.71068 0.0496659L9.40816 0.177656L9.54453 0.499995ZM9.94453 1.3L10.1357 1.59316L10.3972 1.42265L10.2576 1.14347L9.94453 1.3ZM6.24453 5L6.53581 5.19419L6.53981 5.1879L6.24453 5ZM5.24453 10.4L4.99704 10.6475L5.01463 10.6651L5.03453 10.68L5.24453 10.4ZM6.64453 11L6.54396 11.3352L6.55175 11.3376L6.55964 11.3395L6.64453 11ZM9.34453 18.7L9.58212 18.9574L9.59202 18.9475L9.34453 18.7ZM2.04453 18.2L1.78195 18.4317L1.78966 18.4399L2.04453 18.2ZM1.64453 8.39999L1.32669 8.2533L1.32417 8.25904L1.64453 8.39999ZM4.84453 3.7L4.59697 3.45243L4.59108 3.45862L4.84453 3.7ZM22.3445 0.499995L22.6576 0.34347L22.5107 0.0496659L22.2082 0.177656L22.3445 0.499995ZM22.7445 1.3L22.9357 1.59316L23.1972 1.42265L23.0576 1.14347L22.7445 1.3ZM19.0445 5L19.3358 5.19419L19.3398 5.1879L19.0445 5ZM18.0445 10.4L17.797 10.6475L17.8146 10.6651L17.8345 10.68L18.0445 10.4ZM19.4445 11L19.344 11.3352L19.3518 11.3376L19.3596 11.3395L19.4445 11ZM22.1445 18.7L22.3821 18.9574L22.392 18.9475L22.1445 18.7ZM14.8445 18.2L14.582 18.4317L14.5897 18.4399L14.8445 18.2ZM14.4445 8.39999L14.1267 8.2533L14.1242 8.25904L14.4445 8.39999ZM17.6445 3.7L17.397 3.45243L17.3911 3.45862L17.6445 3.7ZM9.23148 0.656519L9.63148 1.45652L10.2576 1.14347L9.85758 0.34347L9.23148 0.656519ZM9.75334 1.00683C8.18046 2.03262 6.91079 3.3011 5.94925 4.81209L6.53981 5.1879C7.44494 3.76556 8.64193 2.56737 10.1357 1.59316L9.75334 1.00683ZM5.95331 4.80585C4.9895 6.25157 4.49453 7.71799 4.49453 9.2H5.19453C5.19453 7.88201 5.6329 6.54842 6.53575 5.19414L5.95331 4.80585ZM4.49453 9.2C4.49453 9.78728 4.64159 10.292 4.99704 10.6475L5.49202 10.1525C5.31414 9.97463 5.19453 9.67938 5.19453 9.2H4.49453ZM5.03453 10.68C5.35368 10.9194 5.87125 11.1334 6.54396 11.3352L6.7451 10.6648C6.08448 10.4666 5.66871 10.2806 5.45453 10.12L5.03453 10.68ZM6.55964 11.3395C7.851 11.6624 8.78014 12.1695 9.38555 12.8354C9.98523 13.4951 10.2945 14.3404 10.2945 15.4H10.9945C10.9945 14.193 10.6372 13.1716 9.90351 12.3646C9.17559 11.5639 8.10473 11.0043 6.72942 10.6604L6.55964 11.3395ZM10.2945 15.4C10.2945 16.6495 9.89198 17.6576 9.09704 18.4525L9.59202 18.9475C10.5304 18.0091 10.9945 16.8172 10.9945 15.4H10.2945ZM9.10713 18.4428C8.31366 19.1752 7.30224 19.55 6.04453 19.55V20.25C7.45349 20.25 8.64207 19.8247 9.58193 18.9572L9.10713 18.4428ZM6.04453 19.55C4.53798 19.55 3.29786 19.021 2.2994 17.9601L1.78966 18.4399C2.92454 19.6457 4.35108 20.25 6.04453 20.25V19.55ZM2.30698 17.9684C1.37773 16.9153 0.894531 15.4725 0.894531 13.6H0.194531C0.194531 15.5941 0.711331 17.218 1.78209 18.4316L2.30698 17.9684ZM0.894531 13.6C0.894531 11.8491 1.25087 10.1637 1.96489 8.54095L1.32417 8.25904C0.571526 9.9696 0.194531 11.7509 0.194531 13.6H0.894531ZM1.96232 8.54666C2.74601 6.84867 3.7908 5.31391 5.09798 3.94137L4.59108 3.45862C3.23159 4.88608 2.14305 6.48466 1.32675 8.25332L1.96232 8.54666ZM5.09202 3.94748C6.46183 2.57768 7.99122 1.5372 9.68091 0.822333L9.40816 0.177656C7.63118 0.929458 6.02724 2.02232 4.59704 3.45251L5.09202 3.94748ZM22.0315 0.656519L22.4315 1.45652L23.0576 1.14347L22.6576 0.34347L22.0315 0.656519ZM22.5533 1.00683C20.9805 2.03262 19.7108 3.3011 18.7493 4.81209L19.3398 5.1879C20.2449 3.76556 21.4419 2.56737 22.9357 1.59316L22.5533 1.00683ZM18.7533 4.80585C17.7895 6.25157 17.2945 7.71799 17.2945 9.2H17.9945C17.9945 7.88201 18.4329 6.54842 19.3358 5.19414L18.7533 4.80585ZM17.2945 9.2C17.2945 9.78728 17.4416 10.292 17.797 10.6475L18.292 10.1525C18.1141 9.97463 17.9945 9.67938 17.9945 9.2H17.2945ZM17.8345 10.68C18.1537 10.9194 18.6712 11.1334 19.344 11.3352L19.5451 10.6648C18.8845 10.4666 18.4687 10.2806 18.2545 10.12L17.8345 10.68ZM19.3596 11.3395C20.651 11.6624 21.5801 12.1695 22.1856 12.8354C22.7852 13.4951 23.0945 14.3404 23.0945 15.4H23.7945C23.7945 14.193 23.4372 13.1716 22.7035 12.3646C21.9756 11.5639 20.9047 11.0043 19.5294 10.6604L19.3596 11.3395ZM23.0945 15.4C23.0945 16.6495 22.692 17.6576 21.897 18.4525L22.392 18.9475C23.3304 18.0091 23.7945 16.8172 23.7945 15.4H23.0945ZM21.9071 18.4428C21.1137 19.1752 20.1022 19.55 18.8445 19.55V20.25C20.2535 20.25 21.4421 19.8247 22.3819 18.9572L21.9071 18.4428ZM18.8445 19.55C17.338 19.55 16.0979 19.021 15.0994 17.9601L14.5897 18.4399C15.7245 19.6457 17.1511 20.25 18.8445 20.25V19.55ZM15.107 17.9684C14.1777 16.9153 13.6945 15.4725 13.6945 13.6H12.9945C12.9945 15.5941 13.5113 17.218 14.5821 18.4316L15.107 17.9684ZM13.6945 13.6C13.6945 11.8491 14.0509 10.1637 14.7649 8.54095L14.1242 8.25904C13.3715 9.9696 12.9945 11.7509 12.9945 13.6H13.6945ZM14.7623 8.54666C15.546 6.84867 16.5908 5.31391 17.898 3.94137L17.3911 3.45862C16.0316 4.88608 14.9431 6.48466 14.1267 8.25332L14.7623 8.54666ZM17.892 3.94748C19.2618 2.57768 20.7912 1.5372 22.4809 0.822333L22.2082 0.177656C20.4312 0.929458 18.8272 2.02232 17.397 3.45251L17.892 3.94748Z"
                        fill={colors.sand}
                      />
                    </svg>
                  </QuoteContainer>
                  <QuoteText>“{slide.quote}”</QuoteText>
                  <Stars />
                  <CustomerInfo>
                    {slide.name} {!!slide.subText && `- ${slide.subText}`}
                  </CustomerInfo>
                </SwiperSlide>
              )
            })}
        </Swiper>
        <ArrowWrapper className="slideNext-btn-review-slider">
          <RightSliderArrow color={colors.sand} size={57} />
        </ArrowWrapper>
      </DesktopSlider>
    </Container>
  )
}

const Container = styled.div<any>`
  overflow: hidden;
  padding: 0 15px;
  .swiper-pagination {
    bottom: 0 !important;
  }
  .swiper-pagination-bullet {
    height: 11px;
    width: 11px;
    margin: 0 6px !important;
    background-color: transparent !important;
    border: 1px solid ${colors.sand} !important;
    opacity: 1 !important;
    ${OutlineOnFocusVisible};
  }
  .swiper-pagination-bullet-active {
    background-color: ${colors.sand} !important;
    border: 1px solid ${colors.sand} !important;
  }
`
const MobileSlider = styled.div<any>`
  display: block;
  overflow: hidden;
  ${(props: any): any => config(props).media.lg`
    display: none;
  `}
`
const DesktopSlider = styled.div<any>`
  display: none;
  ${(props: any): any => config(props).media.lg`
    display: flex;
  `}
`
const QuoteText = styled(H5)<any>`
  text-align: center;
  max-width: 335px;
  margin: 0 auto 20px;
  color: ${colors.sand};
  ${(props: any): any => config(props).media.md`
    max-width: 466px;
  `}
  ${(props: any): any => config(props).media.lg`
    max-width: 698px;
    font-size: 50px;
    line-height: 60px;
      margin: 0 auto 40px;
  `}
  ${(props: any): any => config(props).media.xl`
    max-width: 869px;
  `}
`
const QuoteContainer = styled.div<any>`
  margin: 0 auto 18px;
  text-align: center;

  ${(props: any): any => config(props).media.lg`
    margin: 2px auto 22px;
  `}
`
const CustomerInfo = styled(P)<any>`
  font-size: 12px;
  line-height: 1.25em;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  text-align: center;
  margin: 16px 0 40px;
  color: ${colors.sand};
`
const ArrowWrapper = styled.div<any>`
  display: flex;
  justify-content: center;
  align-items: center;
  will-change: transform;
  transition: transform 0.2s ease;
  transform: translate3d(0px, 0, 0);
  cursor: pointer;
  &.swiper-button-disabled {
    &:hover {
      transform: translate3d(0, 0, 0) !important;
    }
    > svg {
      opacity: 0.8;
    }
  }
  &.slidePrev-btn-review-slider {
    &:hover {
      transform: translate3d(-15px, 0, 0);
    }
    &:focus {
      outline: none;
    }
  }
  &.slideNext-btn-review-slider {
    &:hover {
      transform: translate3d(15px, 0, 0);
    }
    &:focus {
      outline: none;
    }
  }
`
